import React, { useEffect, useMemo } from "react";
import {
  Box,
  Stack,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  CircularProgress,
  Paper,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBackOutlined";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import { useDispatch, useSelector } from "react-redux";
import {
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  fetchCompany,
  fetchCompanyStatuses,
} from "./companiesSlice";

const TAB_VALUES = ["details", "users", "devices"];

export default function CompanyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { current, items, loadingCurrent, statuses } = useSelector(
    (s) => s.companies
  );

  const fromList = items.find((c) => c._id === id);
  const company = current?._id === id ? current : fromList;

  useEffect(() => {
    dispatch(fetchCompany(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (!statuses.length) dispatch(fetchCompanyStatuses());
  }, [dispatch, statuses.length]);

  const activeTab = useMemo(() => {
    const seg = location.pathname.split("/").filter(Boolean).pop();
    return TAB_VALUES.includes(seg) ? seg : "details";
  }, [location.pathname]);

  if (!company) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        {loadingCurrent ? (
          <CircularProgress size={22} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Company not found.
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Tooltip title="Back to companies">
          <IconButton size="small" onClick={() => navigate("/companies")}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Breadcrumbs separator="›" sx={{ fontSize: "0.9375rem" }}>
            <MuiLink
              component="button"
              underline="hover"
              color="text.secondary"
              onClick={() => navigate("/")}
              sx={{ fontSize: "0.9375rem" }}
            >
              Home
            </MuiLink>
            <MuiLink
              component="button"
              underline="hover"
              color="text.secondary"
              onClick={() => navigate("/companies")}
              sx={{ fontSize: "0.9375rem" }}
            >
              Companies
            </MuiLink>
            <Typography
              color="text.primary"
              sx={{ fontSize: "0.9375rem", fontWeight: 600 }}
              noWrap
            >
              {company.name}
            </Typography>
          </Breadcrumbs>
        </Box>
        <Tooltip title="Refresh">
          <IconButton
            size="small"
            onClick={() => dispatch(fetchCompany(id))}
            sx={{ display: { xs: "none", md: "inline-flex" } }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Paper variant="outlined" sx={{ mb: 1.5 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => navigate(`/companies/${id}/${v}`)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, fontSize: "0.75rem" } }}
        >
          <Tab value="details" label="Details" />
          <Tab value="users" label="Users" />
          <Tab value="devices" label="Devices" />
        </Tabs>
      </Paper>

      <Outlet context={{ company }} />
    </Box>
  );
}
